import {
    MERCHANT_PAYOUT_PREVIEW_RECEIVING,
    MERCHANT_PAYOUT_PREVIEW_RECEIVED,
    MERCHANT_PAYOUT_PREVIEW_ERROR,
    MERCHANT_RESERVE_SAVING,
    MERCHANT_RESERVE_SAVED,
    MERCHANT_RESERVE_SAVE_ERROR,
    MERCHANT_RESERVE_RECEIVING,
    MERCHANT_RESERVE_RECEIVED,
    MERCHANT_RESERVE_LOAD_ERROR,
    MERCHANT_PAYOUT_CREATING,
    MERCHANT_PAYOUT_CREATED,
    MERCHANT_PAYOUT_CREATE_ERROR,
    MERCHANT_PAYOUT_ERROR_CLEARED,
    MERCHANT_TIP_PAYOUT_SETTINGS_RECEIVING,
    MERCHANT_TIP_PAYOUT_SETTINGS_RECEIVED,
    MERCHANT_TIP_PAYOUT_SETTINGS_ERROR,
    MERCHANT_TIP_PAYOUT_SETTINGS_SAVING,
    MERCHANT_TIP_PAYOUT_SETTINGS_SAVED,
    MERCHANT_TIP_PAYOUT_SETTINGS_SAVE_ERROR,
    MERCHANT_TIP_PAYOUT_PAYING,
    MERCHANT_TIP_PAYOUT_PAID,
    MERCHANT_TIP_PAYOUT_PAY_ERROR,
    MERCHANT_TIP_PAYOUT_MARK_ALL_RECEIVING,
    MERCHANT_TIP_PAYOUT_MARK_ALL_RECEIVED,
    MERCHANT_TIP_PAYOUT_MARK_ALL_ERROR,
    MERCHANT_TIP_PAYOUT_DIRECT_RECIPIENTS_RECEIVING,
    MERCHANT_TIP_PAYOUT_DIRECT_RECIPIENTS_RECEIVED,
    MERCHANT_TIP_PAYOUT_DIRECT_RECIPIENTS_ERROR,
    MERCHANT_TIP_PAYOUT_DIRECT_PAYING,
    MERCHANT_TIP_PAYOUT_DIRECT_PAID,
    MERCHANT_TIP_PAYOUT_DIRECT_PAY_ERROR,
    MERCHANT_TIP_PAYOUT_DESIGNATED_RECIPIENTS_RECEIVING,
    MERCHANT_TIP_PAYOUT_DESIGNATED_RECIPIENTS_RECEIVED,
    MERCHANT_TIP_PAYOUT_DESIGNATED_RECIPIENTS_ERROR,
    MERCHANT_TIP_PAYOUT_PENDING_BREAKDOWN_RECEIVING,
    MERCHANT_TIP_PAYOUT_PENDING_BREAKDOWN_RECEIVED,
    MERCHANT_TIP_PAYOUT_PENDING_BREAKDOWN_ERROR,
    MERCHANT_TIP_PAYOUT_PAID_TO_MANAGER_BREAKDOWN_RECEIVING,
    MERCHANT_TIP_PAYOUT_PAID_TO_MANAGER_BREAKDOWN_RECEIVED,
    MERCHANT_TIP_PAYOUT_PAID_TO_MANAGER_BREAKDOWN_ERROR,
    MERCHANT_TIP_PAYOUT_RELEASABLE_BREAKDOWN_RECEIVING,
    MERCHANT_TIP_PAYOUT_RELEASABLE_BREAKDOWN_RECEIVED,
    MERCHANT_TIP_PAYOUT_RELEASABLE_BREAKDOWN_ERROR,
    MERCHANT_RECENT_TIP_PAYOUTS_RECEIVING,
    MERCHANT_RECENT_TIP_PAYOUTS_RECEIVED,
    MERCHANT_RECENT_TIP_PAYOUTS_ERROR,
    MERCHANT_RECENT_BANK_PAYOUTS_RECEIVING,
    MERCHANT_RECENT_BANK_PAYOUTS_RECEIVED,
    MERCHANT_RECENT_BANK_PAYOUTS_ERROR,
} from '../actions/merchantPayout';

function mergeTipPayoutSettingsPartial(existing, incoming) {
    if (!incoming) {
        return existing;
    }

    if (!existing) {
        return incoming;
    }

    return {
        ...existing,
        ...incoming,
        designatedRecipient: incoming.designatedRecipient ?? existing.designatedRecipient,
        eligibleRecipients: incoming.eligibleRecipients ?? existing.eligibleRecipients,
        designatedTipPayoutUserId: incoming.designatedTipPayoutUserId !== undefined
            ? incoming.designatedTipPayoutUserId
            : existing?.designatedTipPayoutUserId,
        pendingTipsBreakdown: incoming.pendingTipsBreakdown ?? existing.pendingTipsBreakdown,
        tipsPaidToManagerBreakdown: incoming.tipsPaidToManagerBreakdown ?? existing.tipsPaidToManagerBreakdown,
        releasableTipsBreakdown: incoming.releasableTipsBreakdown ?? existing.releasableTipsBreakdown,
        pendingTipsAndGratuity: incoming.pendingTipsAndGratuity !== undefined
            ? incoming.pendingTipsAndGratuity
            : existing.pendingTipsAndGratuity,
        releasableTipsAndGratuity: incoming.releasableTipsAndGratuity !== undefined
            ? incoming.releasableTipsAndGratuity
            : existing.releasableTipsAndGratuity,
        tipsAndGratuityPaidToManager: incoming.tipsAndGratuityPaidToManager !== undefined
            ? incoming.tipsAndGratuityPaidToManager
            : existing.tipsAndGratuityPaidToManager,
    };
}

function applyManagerReleaseObligationsToSettings(settings, result) {
    if (!settings || !result) {
        return settings;
    }

    const updates = result.obligationUpdates || result.ObligationUpdates;
    if (!Array.isArray(updates) || updates.length === 0) {
        return settings;
    }

    const updateByKey = new Map();
    updates.forEach((raw) => {
        const userId = Number(raw?.userId ?? raw?.UserId ?? 0);
        const workPeriodId = Number(raw?.workPeriodId ?? raw?.WorkPeriodId ?? 0);
        if (!userId || !workPeriodId) {
            return;
        }

        updateByKey.set(`${userId}-${workPeriodId}`, {
            status: String(raw?.status ?? raw?.Status ?? '').toLowerCase(),
            tipsOwedAmount: Number(raw?.tipsOwedAmount ?? raw?.TipsOwedAmount ?? 0),
        });
    });

    if (updateByKey.size === 0) {
        return settings;
    }

    const releasableTipsBreakdown = (settings.releasableTipsBreakdown || [])
        .map((row) => {
            const key = `${row.userId}-${row.workPeriodId}`;
            const update = updateByKey.get(key);
            if (!update) {
                return row;
            }

            if (update.status === 'paid') {
                return null;
            }

            const amountReleasable = Math.max(0, update.tipsOwedAmount);
            if (amountReleasable <= 0) {
                return null;
            }

            return { ...row, amountReleasable };
        })
        .filter(Boolean);

    return {
        ...settings,
        releasableTipsBreakdown,
    };
}

const INSTANT_PREVIEW_FIELDS = [
    'instantAvailableBalance',
    'instantPayoutEligible',
    'instantPayoutAvailable',
    'instantPayoutFeePercent',
    'instantPayoutFeeFixed',
    'estimatedInstantFee',
];

function previewIncludesInstantDetails(preview) {
    if (!preview) {
        return false;
    }

    return !!preview.instantPayoutAvailable
        || (preview.instantPayoutEligible ?? 0) > 0
        || (preview.instantAvailableBalance ?? 0) > 0;
}

function mergePreviewPreserveInstant(existing, incoming) {
    if (!incoming) {
        return existing || null;
    }

    if (!existing) {
        return incoming;
    }

    if (previewIncludesInstantDetails(incoming)) {
        return { ...existing, ...incoming };
    }

    const merged = { ...existing, ...incoming };
    INSTANT_PREVIEW_FIELDS.forEach((field) => {
        merged[field] = existing[field];
    });
    return merged;
}

const initialState = {
    preview: null,
    previewLoading: false,
    previewError: '',
    reserveConfig: null,
    reserveLoading: false,
    reserveLoadError: '',
    reserveSaving: false,
    reserveError: '',
    payoutCreating: false,
    payoutError: '',
    lastPayoutResult: null,
    tipPayoutSettings: null,
    tipPayoutSettingsLoading: false,
    tipPayoutSettingsError: '',
    tipPayoutSettingsSaving: false,
    tipPayoutSettingsSaveError: '',
    tipPayoutPaying: false,
    tipPayoutPayError: '',
    tipPayoutMarkAllLoading: false,
    tipPayoutMarkAllError: '',
    lastTipPayoutResult: null,
    tipPayoutDirectRecipients: null,
    tipPayoutDirectRecipientsLoading: false,
    tipPayoutDirectRecipientsError: '',
    tipPayoutDirectPaying: false,
    tipPayoutDirectPayError: '',
    tipPayoutDirectPayingUserId: null,
    lastDirectTipPayoutResult: null,
    tipPayoutDesignatedRecipientsLoading: false,
    tipPayoutDesignatedRecipientsError: '',
    tipPayoutPendingBreakdownLoading: false,
    tipPayoutPendingBreakdownError: '',
    tipPayoutPaidToManagerBreakdownLoading: false,
    tipPayoutPaidToManagerBreakdownError: '',
    tipPayoutReleasableBreakdownLoading: false,
    tipPayoutReleasableBreakdownError: '',
    recentSuccessfulTipPayouts: [],
    recentSuccessfulTipPayoutsLoading: false,
    recentSuccessfulTipPayoutsError: '',
    recentMerchantBankPayouts: [],
    recentMerchantBankPayoutsLoading: false,
    recentMerchantBankPayoutsError: '',
    tipPayoutSettingsDateRangeKey: 'all',
    tipPayoutDirectRecipientsDateRangeKey: 'all',
    tipPayoutSummaryCache: {},
};

export default function merchantPayout(state = initialState, action) {
    switch (action.type) {
        case MERCHANT_PAYOUT_PREVIEW_RECEIVING:
            return {
                ...state,
                previewLoading: true,
                previewError: '',
            };
        case MERCHANT_PAYOUT_PREVIEW_RECEIVED: {
            const preview = action.payload?.preview || null;

            return {
                ...state,
                previewLoading: false,
                preview,
                previewError: '',
                tipPayoutSummaryCache: {},
            };
        }
        case MERCHANT_PAYOUT_PREVIEW_ERROR:
            return {
                ...state,
                previewLoading: false,
                previewError: action.payload?.error || 'Failed to load payout preview.',
            };
        case MERCHANT_RESERVE_SAVING:
            return {
                ...state,
                reserveSaving: true,
                reserveError: '',
            };
        case MERCHANT_RESERVE_RECEIVING:
            return {
                ...state,
                reserveLoading: true,
                reserveLoadError: '',
            };
        case MERCHANT_RESERVE_RECEIVED: {
            const config = action.payload?.config || null;
            return {
                ...state,
                reserveLoading: false,
                reserveConfig: config,
                reserveLoadError: '',
                preview: config && state.preview
                    ? { ...state.preview, config }
                    : state.preview,
            };
        }
        case MERCHANT_RESERVE_LOAD_ERROR:
            return {
                ...state,
                reserveLoading: false,
                reserveLoadError: action.payload?.error || 'Failed to load reserve settings.',
            };
        case MERCHANT_RESERVE_SAVED:
            return {
                ...state,
                reserveSaving: false,
                reserveConfig: action.payload?.config || action.payload?.preview?.config || state.reserveConfig,
                preview: action.payload?.preview
                    ? mergePreviewPreserveInstant(state.preview, action.payload.preview)
                    : (action.payload?.config && state.preview
                        ? { ...state.preview, config: action.payload.config }
                        : state.preview),
                reserveError: '',
            };
        case MERCHANT_RESERVE_SAVE_ERROR:
            return {
                ...state,
                reserveSaving: false,
                reserveError: action.payload?.error || 'Failed to save reserve settings.',
            };
        case MERCHANT_PAYOUT_CREATING:
            return {
                ...state,
                payoutCreating: true,
                payoutError: '',
            };
        case MERCHANT_PAYOUT_CREATED:
            return {
                ...state,
                payoutCreating: false,
                preview: mergePreviewPreserveInstant(state.preview, action.payload?.preview),
                lastPayoutResult: action.payload?.result || null,
                payoutError: '',
            };
        case MERCHANT_PAYOUT_CREATE_ERROR:
            return {
                ...state,
                payoutCreating: false,
                payoutError: action.payload?.error || 'Failed to create payout.',
            };
        case MERCHANT_PAYOUT_ERROR_CLEARED:
            return {
                ...state,
                payoutError: '',
            };
        case MERCHANT_TIP_PAYOUT_SETTINGS_RECEIVING:
            return {
                ...state,
                tipPayoutSettingsLoading: true,
                tipPayoutSettingsError: '',
                tipPayoutSettingsDateRangeKey: action.payload?.dateRangeKey || state.tipPayoutSettingsDateRangeKey,
            };
        case MERCHANT_TIP_PAYOUT_SETTINGS_RECEIVED: {
            const mergedSettings = mergeTipPayoutSettingsPartial(
                state.tipPayoutSettings,
                action.payload?.settings || null,
            );
            const dateRangeKey = action.payload?.dateRangeKey || state.tipPayoutSettingsDateRangeKey;
            return {
                ...state,
                tipPayoutSettingsLoading: false,
                tipPayoutSettings: mergedSettings,
                preview: mergePreviewPreserveInstant(state.preview, action.payload?.preview),
                tipPayoutSettingsDateRangeKey: dateRangeKey,
                tipPayoutSettingsError: '',
                tipPayoutSummaryCache: dateRangeKey && mergedSettings
                    ? {
                        ...state.tipPayoutSummaryCache,
                        [dateRangeKey]: {
                            pendingTipsAndGratuity: mergedSettings.pendingTipsAndGratuity,
                            releasableTipsAndGratuity: mergedSettings.releasableTipsAndGratuity,
                            reservedTipsAndGratuity: mergedSettings.reservedTipsAndGratuity,
                            tipsAndGratuityPaidToManager: mergedSettings.tipsAndGratuityPaidToManager,
                            summaryVersion: 6,
                        },
                    }
                    : state.tipPayoutSummaryCache,
            };
        }
        case MERCHANT_TIP_PAYOUT_SETTINGS_ERROR:
            return {
                ...state,
                tipPayoutSettingsLoading: false,
                tipPayoutSettingsError: action.payload?.error || 'Failed to load tip payout settings.',
            };
        case MERCHANT_TIP_PAYOUT_SETTINGS_SAVING:
            return {
                ...state,
                tipPayoutSettingsSaving: true,
                tipPayoutSettingsSaveError: '',
            };
        case MERCHANT_TIP_PAYOUT_SETTINGS_SAVED:
            return {
                ...state,
                tipPayoutSettingsSaving: false,
                tipPayoutSettings: mergeTipPayoutSettingsPartial(
                    state.tipPayoutSettings,
                    action.payload?.settings || null,
                ),
                preview: mergePreviewPreserveInstant(state.preview, action.payload?.preview),
                tipPayoutSettingsSaveError: '',
            };
        case MERCHANT_TIP_PAYOUT_SETTINGS_SAVE_ERROR:
            return {
                ...state,
                tipPayoutSettingsSaving: false,
                tipPayoutSettingsSaveError: action.payload?.error || 'Failed to save tip payout recipient.',
            };
        case MERCHANT_TIP_PAYOUT_PAYING:
            return {
                ...state,
                tipPayoutPaying: true,
                tipPayoutPayError: '',
            };
        case MERCHANT_TIP_PAYOUT_PAID: {
            const mergedPaidSettings = applyManagerReleaseObligationsToSettings(
                mergeTipPayoutSettingsPartial(
                    state.tipPayoutSettings,
                    action.payload?.settings || null,
                ),
                action.payload?.result,
            );
            return {
                ...state,
                tipPayoutPaying: false,
                tipPayoutSettings: mergedPaidSettings,
                preview: mergePreviewPreserveInstant(state.preview, action.payload?.preview),
                lastTipPayoutResult: action.payload?.result || null,
                tipPayoutPayError: '',
                tipPayoutSummaryCache: {},
            };
        }
        case MERCHANT_TIP_PAYOUT_PAY_ERROR:
            return {
                ...state,
                tipPayoutPaying: false,
                tipPayoutSettings: mergeTipPayoutSettingsPartial(
                    state.tipPayoutSettings,
                    action.payload?.settings || null,
                ),
                preview: mergePreviewPreserveInstant(state.preview, action.payload?.preview),
                tipPayoutPayError: action.payload?.error || 'Failed to pay tips to recipient.',
            };
        case MERCHANT_TIP_PAYOUT_MARK_ALL_RECEIVING:
            return {
                ...state,
                tipPayoutMarkAllLoading: true,
                tipPayoutMarkAllError: '',
            };
        case MERCHANT_TIP_PAYOUT_MARK_ALL_RECEIVED:
            return {
                ...state,
                tipPayoutMarkAllLoading: false,
                tipPayoutSettings: mergeTipPayoutSettingsPartial(
                    state.tipPayoutSettings,
                    action.payload?.settings || null,
                ),
                preview: mergePreviewPreserveInstant(state.preview, action.payload?.preview),
                tipPayoutMarkAllError: '',
                tipPayoutSummaryCache: {},
            };
        case MERCHANT_TIP_PAYOUT_MARK_ALL_ERROR:
            return {
                ...state,
                tipPayoutMarkAllLoading: false,
                tipPayoutMarkAllError: action.payload?.error || 'Failed to mark outstanding tips as paid.',
            };
        case MERCHANT_TIP_PAYOUT_DIRECT_RECIPIENTS_RECEIVING:
            return {
                ...state,
                tipPayoutDirectRecipientsLoading: true,
                tipPayoutDirectRecipientsError: '',
                tipPayoutDirectRecipientsDateRangeKey: action.payload?.dateRangeKey || state.tipPayoutDirectRecipientsDateRangeKey,
            };
        case MERCHANT_TIP_PAYOUT_DIRECT_RECIPIENTS_RECEIVED:
            return {
                ...state,
                tipPayoutDirectRecipientsLoading: false,
                tipPayoutDirectRecipients: action.payload?.recipients || [],
                tipPayoutDirectRecipientsDateRangeKey: action.payload?.dateRangeKey || state.tipPayoutDirectRecipientsDateRangeKey,
                tipPayoutDirectRecipientsError: '',
            };
        case MERCHANT_TIP_PAYOUT_DIRECT_RECIPIENTS_ERROR:
            return {
                ...state,
                tipPayoutDirectRecipientsLoading: false,
                tipPayoutDirectRecipientsError: action.payload?.error || 'Failed to load direct tip payout recipients.',
            };
        case MERCHANT_TIP_PAYOUT_DIRECT_PAYING:
            return {
                ...state,
                tipPayoutDirectPaying: true,
                tipPayoutDirectPayingUserId: action.payload?.userId ?? null,
                tipPayoutDirectPayError: '',
            };
        case MERCHANT_TIP_PAYOUT_DIRECT_PAID:
            return {
                ...state,
                tipPayoutDirectPaying: false,
                tipPayoutDirectPayingUserId: null,
                tipPayoutDirectRecipients: action.payload?.recipients || state.tipPayoutDirectRecipients,
                lastDirectTipPayoutResult: action.payload?.result || null,
                tipPayoutDirectPayError: '',
                tipPayoutSummaryCache: {},
            };
        case MERCHANT_TIP_PAYOUT_DIRECT_PAY_ERROR:
            return {
                ...state,
                tipPayoutDirectPaying: false,
                tipPayoutDirectPayingUserId: null,
                tipPayoutDirectRecipients: action.payload?.recipients || state.tipPayoutDirectRecipients,
                tipPayoutDirectPayError: action.payload?.error || 'Failed to pay tips to employee.',
                tipPayoutSummaryCache: {},
            };
        case MERCHANT_TIP_PAYOUT_DESIGNATED_RECIPIENTS_RECEIVING:
            return {
                ...state,
                tipPayoutDesignatedRecipientsLoading: true,
                tipPayoutDesignatedRecipientsError: '',
            };
        case MERCHANT_TIP_PAYOUT_DESIGNATED_RECIPIENTS_RECEIVED:
            return {
                ...state,
                tipPayoutDesignatedRecipientsLoading: false,
                tipPayoutSettings: mergeTipPayoutSettingsPartial(state.tipPayoutSettings, action.payload?.partial),
                tipPayoutDesignatedRecipientsError: '',
            };
        case MERCHANT_TIP_PAYOUT_DESIGNATED_RECIPIENTS_ERROR:
            return {
                ...state,
                tipPayoutDesignatedRecipientsLoading: false,
                tipPayoutDesignatedRecipientsError: action.payload?.error || 'Failed to load designated tip recipients.',
            };
        case MERCHANT_TIP_PAYOUT_PENDING_BREAKDOWN_RECEIVING:
            return {
                ...state,
                tipPayoutPendingBreakdownLoading: true,
                tipPayoutPendingBreakdownError: '',
            };
        case MERCHANT_TIP_PAYOUT_PENDING_BREAKDOWN_RECEIVED:
            return {
                ...state,
                tipPayoutPendingBreakdownLoading: false,
                tipPayoutSettings: mergeTipPayoutSettingsPartial(state.tipPayoutSettings, {
                    pendingTipsBreakdown: action.payload?.pendingTipsBreakdown || [],
                }),
                tipPayoutPendingBreakdownError: '',
            };
        case MERCHANT_TIP_PAYOUT_PENDING_BREAKDOWN_ERROR:
            return {
                ...state,
                tipPayoutPendingBreakdownLoading: false,
                tipPayoutPendingBreakdownError: action.payload?.error || 'Failed to load pending tips breakdown.',
            };
        case MERCHANT_TIP_PAYOUT_PAID_TO_MANAGER_BREAKDOWN_RECEIVING:
            return {
                ...state,
                tipPayoutPaidToManagerBreakdownLoading: true,
                tipPayoutPaidToManagerBreakdownError: '',
            };
        case MERCHANT_TIP_PAYOUT_PAID_TO_MANAGER_BREAKDOWN_RECEIVED:
            return {
                ...state,
                tipPayoutPaidToManagerBreakdownLoading: false,
                tipPayoutSettings: mergeTipPayoutSettingsPartial(state.tipPayoutSettings, {
                    tipsPaidToManagerBreakdown: action.payload?.tipsPaidToManagerBreakdown || [],
                }),
                tipPayoutPaidToManagerBreakdownError: '',
            };
        case MERCHANT_TIP_PAYOUT_PAID_TO_MANAGER_BREAKDOWN_ERROR:
            return {
                ...state,
                tipPayoutPaidToManagerBreakdownLoading: false,
                tipPayoutPaidToManagerBreakdownError:
                    action.payload?.error || 'Failed to load tips paid to manager breakdown.',
            };
        case MERCHANT_TIP_PAYOUT_RELEASABLE_BREAKDOWN_RECEIVING:
            return {
                ...state,
                tipPayoutReleasableBreakdownLoading: true,
                tipPayoutReleasableBreakdownError: '',
            };
        case MERCHANT_TIP_PAYOUT_RELEASABLE_BREAKDOWN_RECEIVED:
            return {
                ...state,
                tipPayoutReleasableBreakdownLoading: false,
                tipPayoutSettings: mergeTipPayoutSettingsPartial(state.tipPayoutSettings, {
                    releasableTipsBreakdown: action.payload?.releasableTipsBreakdown || [],
                }),
                tipPayoutReleasableBreakdownError: '',
            };
        case MERCHANT_TIP_PAYOUT_RELEASABLE_BREAKDOWN_ERROR:
            return {
                ...state,
                tipPayoutReleasableBreakdownLoading: false,
                tipPayoutReleasableBreakdownError: action.payload?.error || 'Failed to load releasable tips breakdown.',
            };
        case MERCHANT_RECENT_TIP_PAYOUTS_RECEIVING:
            return {
                ...state,
                recentSuccessfulTipPayoutsLoading: true,
                recentSuccessfulTipPayoutsError: '',
            };
        case MERCHANT_RECENT_TIP_PAYOUTS_RECEIVED:
            return {
                ...state,
                recentSuccessfulTipPayoutsLoading: false,
                recentSuccessfulTipPayouts: action.payload?.payouts || [],
                recentSuccessfulTipPayoutsError: '',
            };
        case MERCHANT_RECENT_TIP_PAYOUTS_ERROR:
            return {
                ...state,
                recentSuccessfulTipPayoutsLoading: false,
                recentSuccessfulTipPayoutsError: action.payload?.error || 'Failed to load recent tip payouts.',
            };
        case MERCHANT_RECENT_BANK_PAYOUTS_RECEIVING:
            return {
                ...state,
                recentMerchantBankPayoutsLoading: true,
                recentMerchantBankPayoutsError: '',
            };
        case MERCHANT_RECENT_BANK_PAYOUTS_RECEIVED:
            return {
                ...state,
                recentMerchantBankPayoutsLoading: false,
                recentMerchantBankPayouts: action.payload?.payouts || [],
                recentMerchantBankPayoutsError: '',
            };
        case MERCHANT_RECENT_BANK_PAYOUTS_ERROR:
            return {
                ...state,
                recentMerchantBankPayoutsLoading: false,
                recentMerchantBankPayoutsError: action.payload?.error || 'Failed to load recent payouts.',
            };
        default:
            return state;
    }
}
